import Vue from "vue";
import fetch from "../../../../utils/fetch";
import { legendObservationsCount } from "./legendObservationsCount";
import { legendNumericData } from "./legendNumericData";
import { legendCategoricalData } from "./legendCategoricalData";

const rangeNoData = {
  text: "No Data",
  symbol: { type: "far fa-circle", size: 10, text: null },
  color: "rgb(194, 194, 194)",
  backgroundColor: "rgb(44, 62, 80)",
};
const rangeNoObservations = {
  text: "No Observations",
  symbol: { type: "far fa-circle", size: 10, text: null },
  color: "rgb(44, 62, 80)",
  backgroundColor: "rgb(194, 194, 194)",
};

const getInitialState = () => {
  return {
    projectId: null,
    locationsIsLoading: false,
    message: null,
    messageType: null,
    locations: {},
    locationsTotal: null, // keep track of the locations that are on the server
    locationsLoaded: 0, // Number of locations loaded from server

    // keep track of the record that was last selected
    // Type of the selected datasheet record
    // (number, dropdown, radio, datasheet, observation, datasheets etc.)
    resultType: "observation",
    resultId: null,
    resultLabel: null, // Label of the selected record. Either the datasheet name or record label
    resultDescription: null, // Description of the selected record. Either the datasheet description or record description
    resultsIsLoading: false,
    // list of cached record IDs to avoid re-fetching
    // or at least immediately display the data if already client side
    resultsCacheList: [],

    // Keeps track of the selected aggregation methods for different record types.
    // This is seperate so that switching records types stays consistent.
    aggregationNumeric: "avg", // avg, median, most_recent, or total (for numeric data)
    aggregationCategorical: "most_frequent", // most_frequent or most_recent (for categorical data)
    aggregationDatasheetUsage: "most_frequent", // most_frequent or most_recent (for datasheet-level data)
    displayNoData: true, // Whether to display locations without data (default true)

    // Legend information for tying the display on the legend to the markers
    legendRanges: [],
    legendScale: null, // equal_interval, equal_count, natural_breaks
    legendMin: null,
    legendMax: null,
    legendWholeNumber: true,
    legendMaxCharacters: 0,
    legendUseFullValues: false,

    // markersInView is a count of the markers visible in the map
    markersInView: null,
  };
};

const getters = {
  locations: (state) =>
    Object.values(state.locations).map((loc) => {
      // value is the current property selected for legend/coloring
      const value = () => {
        if (state.resultType === "observation") {
          return loc.observationsTotal || 0;
        } else if (state.resultType === "datasheet" && state.resultId) {
          if (loc.results && loc.results[state.resultId]) {
            return loc.results[state.resultId].observationsTotal || 0;
          }
          return 0;
        } else if (state.resultType === "number" && state.resultId) {
          // For numeric records, get the value based on aggregation method
          if (loc.results && loc.results[state.resultId]) {
            const result = loc.results[state.resultId];
            const value = result[state.aggregationNumeric];
            return value !== null && value !== undefined ? value : null;
          }
          return null;
        } else if (
          (state.resultType === "dropdown" || state.resultType === "radio") &&
          state.resultId
        ) {
          // For categorical records, get the option_id based on aggregation method
          if (loc.results && loc.results[state.resultId]) {
            const result = loc.results[state.resultId];
            return state.aggregationCategorical === "most_recent"
              ? result.most_recent_option_id
              : result.most_frequent_option_id;
          }
          return null;
        } else if (state.resultType === "datasheet_usage") {
          // For datasheet usage, get the datasheet_id based on aggregation method
          if (loc.datasheetUsage) {
            return state.aggregationDatasheetUsage === "most_recent"
              ? loc.datasheetUsage.most_recent_datasheet_id
              : loc.datasheetUsage.most_frequent_datasheet_id;
          }
          return null;
        }
        return null;
      };
      return {
        ...loc,
        value: value(),
      };
    }),

  legendTitle: (state) => {
    const labelsNumeric = (value) => {
      const labels = {
        avg: "Average",
        median: "Median",
        most_recent: "Most Recent",
        total: "Total",
      };
      return labels[value] || "Unknown";
    };

    const labelsCategorical = (value) => {
      const labels = {
        most_frequent: "Most Frequent",
        most_recent: "Most Recent",
      };
      return labels[value] || "Unknown";
    };

    if (state.resultType === "observation") {
      return "Observations";
    } else if (state.resultType === "datasheet" && state.resultId) {
      return `Observations`;
    } else if (state.resultType === "number") {
      return `${labelsNumeric(state.aggregationNumeric)} ${state.resultLabel}`;
    } else if (
      state.resultType === "dropdown" ||
      state.resultType === "radio"
    ) {
      return `${labelsCategorical(state.aggregationCategorical)} ${state.resultLabel}`;
    } else if (state.resultType === "datasheet_usage") {
      return `${labelsCategorical(state.aggregationDatasheetUsage)} Datasheet`;
    } else {
      return "Unknown Result Type";
    }
  },
  legendSubtitle: (state) => {
    const scaleLabel = (value) => {
      const labels = {
        linear: "on a Linear scale",
        equal_interval: "on an Equal Interval scale",
        quartile: "on a Quartile scale",
        equal_count: "on an Equal Count scale",
        natural_breaks: "on a Natural Breaks scale",
      };
      return labels[value] || "on an Unknown scale";
    };

    if (state.resultType === "observation") {
      return "Across the project";
    } else if (state.resultType === "datasheet" && state.resultId) {
      return `Reported for "${state.resultLabel}" ${scaleLabel(state.legendScale)}`;
    } else if (state.resultType === "number" && state.resultLabel) {
      return `${scaleLabel(state.legendScale)}`;
    } else if (
      (state.resultType === "dropdown" || state.resultType === "radio") &&
      state.resultLabel
    ) {
      return `Reported for each location`;
    } else if (state.resultType === "datasheet_usage") {
      return "Used at each location";
    }
  },
  legendTooltip: (state) => {
    if (state.resultType === "observation") {
      return "Total number of observations recorded at each location. These are separated into categories based on natural breaks in the number of observations.";
    }
    if (state.resultType === "datasheet") {
      return `Total number of observations for the "${state.resultLabel}" recorded at each location. These are separated into categories based on natural breaks in the number of observations.`;
    }
    if (state.resultType === "datasheet_usage") {
      return `The datasheet that has been ${
        state.aggregationDatasheetUsage === "recent"
          ? "most recently used"
          : "most frequently used"
      } at each location.`;
    }

    const prefix = state.resultDescription
      ? `Observers were asked to "${state.resultDescription}" `
      : "";

    if (state.resultType === "number") {
      if (state.aggregationNumeric === "total") {
        return `${prefix}The numeric input for "${state.resultLabel}" is summed together for every observation at that location. This was done automatically and may not be appropriate for this specific question. This is separated into categories based on natural separation of values within the underlying data.`;
      }
      if (state.aggregationNumeric === "avg") {
        return `${prefix}The numeric input for "${state.resultLabel}" is the mean value reported at that location. Each category has the same width.`;
      }
      if (state.aggregationNumeric === "median") {
        return `${prefix}The numeric input for "${state.resultLabel}" is the median value reported at that location. This is separated into categories with roughly equal numbers of locations.`;
      }
      if (state.aggregationNumeric === "most_recent") {
        return `${prefix}The most recent result for "${state.resultLabel}" is displayed for each location. This is separated into categories based on natural separation of values within the underlying data.`;
      }
    }
    if (state.resultType === "dropdown" || state.resultType === "radio") {
      return `${prefix}The ${
        state.aggregationCategorical === "most_recent"
          ? "most recent"
          : "most frequent"
      } reported "${
        state.resultLabel
      }" for each location is displayed. The coloring scheme and any alphabet labels (A,B,C...) are generated automatically.`;
    }

    return null;
  },
  legendRangeNoData: (state) => {
    if (
      state.resultType === "observation" ||
      state.resultType === "datasheet"
    ) {
      return rangeNoObservations;
    }
    return rangeNoData;
  },
  legend: (state, getters) => {
    // used within the map legend component
    const fullLegend = {
      title: getters.legendTitle,
      subtitle: getters.legendSubtitle,
      tooltip: getters.legendTooltip,
      ranges: state.legendRanges,
      rangeNoData: getters.legendRangeNoData,
      scale: state.legendScale,
      min: state.legendMin,
      max: state.legendMax,
      wholeNumber: state.legendWholeNumber,
      maxCharacters: state.legendMaxCharacters,
      useFullValues: state.legendUseFullValues,
    };
    console.log("legend getter", fullLegend);
    return fullLegend;
  },
  /**
   * Get legend ranges as a lookup function for observation count
   * @returns {Function} - Function that takes observation count and returns range
   */
  legendLookup: (state) => (value) => {
    if (!state.legendRanges || value === null || value === undefined)
      return rangeNoData;

    if (
      state.resultType === "observation" ||
      state.resultType === "datasheet"
    ) {
      // we know that these observation count ranges are discrete values and inclusive of min, max, one observation and zero observations
      for (const range of state.legendRanges) {
        if (value >= range.min && value <= range.max) {
          return range;
        }
      }
      return rangeNoObservations;
    }
    if (state.resultType === "number") {
      // for numeric data, ranges are continuous. With the max of the last range being inclusive
      // ranges must be in assending order for this to work correctly
      for (let i = 0; i < state.legendRanges.length; i++) {
        const range = state.legendRanges[i];
        const nextRange = state.legendRanges[i + 1];
        if (nextRange) {
          if (value >= range.min && value < nextRange.min) {
            return range;
          }
        } else {
          // last range, inclusive of max
          if (value >= range.min && value <= range.max) {
            return range;
          }
        }
      }
    }
    if (
      state.resultType === "dropdown" ||
      state.resultType === "radio" ||
      state.resultType === "datasheet_usage"
    ) {
      // for categorical data, match the option id or datasheet id
      for (const range of state.legendRanges) {
        if (range.category && range.category.id === value) {
          return range;
        }
      }
    }
    return rangeNoData;
  },
  fetchedCount: (state) => state.locationsLoaded,
  fetchedPercent: (state, getters) => {
    if (!state.locationsTotal) return 0;
    return Math.round((getters.fetchedCount / state.locationsTotal) * 100);
  },
  markerType: (state) => {
    if (state.markersInView > 9600) {
      return "xs";
    } else if (state.markersInView > 2400) {
      return "sm";
    } else if (state.markersInView > 800) {
      return "md";
    } else if (state.markersInView > 200) {
      return "lg";
    } else {
      // less than 200 or null
      return "xl";
    }
  },
};

const t = {
  LOCATIONS_RESET: "LOCATIONS_RESET",
  LOCATIONS_SET_MESSAGE: "LOCATIONS_SET_MESSAGE",

  LOCATION_SET_AGGREGATION_NUMERIC: "LOCATION_SET_AGGREGATION_NUMERIC",
  LOCATION_SET_AGGREGATION_CATEGORICAL: "LOCATION_SET_AGGREGATION_CATEGORICAL",
  LOCATION_SET_AGGREGATION_DATASHEET_USAGE:
    "LOCATION_SET_AGGREGATION_DATASHEET_USAGE",
  LOCATION_SET_DISPLAY_NO_DATA: "LOCATION_SET_DISPLAY_NO_DATA",

  LOCATIONS_START_LOADING: "LOCATIONS_START_LOADING",
  LOCATIONS_ADD: "LOCATIONS_ADD",
  LOCATIONS_UPDATE_LOADED_COUNT: "LOCATIONS_UPDATE_LOADED_COUNT",
  LOCATIONS_FINISH_LOADING: "LOCATIONS_FINISH_LOADING",
  LOCATIONS_SET_OBSERVATIONS: "LOCATIONS_SET_OBSERVATIONS",

  LOCATIONS_START_LOADING_RESULTS: "LOCATIONS_START_LOADING_RESULTS",
  LOCATIONS_ADD_RESULTS: "LOCATIONS_ADD_RESULTS",

  LOCATIONS_START_LOADING_DATASHEET_RESULTS:
    "LOCATIONS_START_LOADING_DATASHEET_RESULTS",
  LOCATIONS_ADD_DATASHEET_RESULTS: "LOCATIONS_ADD_DATASHEET_RESULTS",

  LOCATIONS_START_LOADING_DATASHEET_USAGE:
    "LOCATIONS_START_LOADING_DATASHEET_USAGE",
  LOCATIONS_ADD_DATASHEET_USAGE: "LOCATIONS_ADD_DATASHEET_USAGE",

  LOCATIONS_SET_MARKERS_IN_VIEW: "LOCATIONS_SET_MARKERS_IN_VIEW",
};

const mutations = {
  [t.LOCATIONS_RESET](state) {
    Object.assign(state, getInitialState());
  },
  [t.LOCATIONS_SET_MESSAGE](state, { message, messageType }) {
    state.message = message;
    state.messageType = messageType;
    state.locationsIsLoading = false;
  },

  [t.LOCATION_SET_AGGREGATION_NUMERIC](state, method) {
    state.aggregationNumeric = method;

    const locations = Object.values(state.locations);
    const values = locations
      .filter((loc) => loc.results && loc.results[state.resultId])
      .map((loc) => loc.results[state.resultId][method])
      .filter((val) => val !== null && val !== undefined);
    const legend = legendNumericData(values, method);
    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;
  },
  [t.LOCATION_SET_AGGREGATION_CATEGORICAL](state, method) {
    state.aggregationCategorical = method;
    // regenerate just to update the count
    const locations = Object.values(state.locations);
    const options = state.legendRanges.map((range) => range.category);
    const categoryValues = locations
      .filter((loc) => loc.results && loc.results[state.resultId])
      .map((loc) =>
        method === "most_recent"
          ? loc.results[state.resultId].most_recent_option_id
          : loc.results[state.resultId].most_frequent_option_id,
      );
    const legend = legendCategoricalData(options, categoryValues);
    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;
  },
  [t.LOCATION_SET_AGGREGATION_DATASHEET_USAGE](state, method) {
    state.aggregationDatasheetUsage = method;
  },
  [t.LOCATION_SET_DISPLAY_NO_DATA](state, displayNoData) {
    state.displayNoData = displayNoData;
  },

  [t.LOCATIONS_START_LOADING](state, projectId) {
    state.projectId = projectId;
    state.locationsIsLoading = true;
    state.message = null;
    state.messageType = null;

    state.locations = {};
    state.locationsTotal = null;
    state.locationsLoaded = 0;
    state.displayNoData = true;

    state.resultType = "observation";
    state.resultId = null;
    state.resultLabel = null;
    state.resultDescription = null;

    state.legendRanges = [];
    state.legendScale = null;
    state.legendMin = null;
    state.legendMax = null;
    state.legendWholeNumber = true;
    state.legendMaxCharacters = 0;
    state.legendUseFullValues = false;
  },
  [t.LOCATIONS_ADD](state, response) {
    const newLocations = response["hydra:member"];
    // Directly mutate the locations object for performance
    // No need to copy all existing locations each time
    newLocations.forEach((location) => {
      Vue.set(state.locations, location["id"], location);
    });
    state.locationsTotal = response["hydra:totalItems"];
    const counts = Object.values(state.locations).map(
      (loc) => loc.observationsTotal || 0,
    );
    const legend = legendObservationsCount(counts);
    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;
  },
  [t.LOCATIONS_UPDATE_LOADED_COUNT](state, count) {
    state.locationsLoaded = count;
  },
  [t.LOCATIONS_FINISH_LOADING](state) {
    state.locationsIsLoading = false;
  },
  [t.LOCATIONS_SET_OBSERVATIONS](state) {
    state.resultType = "observation";
    state.resultId = null;
    state.resultLabel = null;
    state.resultDescription = null;
    state.resultsIsLoading = false;
    state.displayNoData = true;

    const legend = legendObservationsCount(
      state.locations
        ? Object.values(state.locations).map(
            (loc) => loc.observationsTotal || 0,
          )
        : [],
    );

    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;
  },

  [t.LOCATIONS_START_LOADING_RESULTS](state, datasheetRecord) {
    state.message = null;
    state.messageType = null;

    state.resultType = datasheetRecord.recordType;
    state.resultId = datasheetRecord.id;
    state.resultLabel = datasheetRecord.label;
    state.resultDescription = datasheetRecord.description;
    state.resultsIsLoading = true;
    state.displayNoData = false;

    state.legendRanges = [];
    state.legendScale = null;
    state.legendMin = null;
    state.legendMax = null;
    state.legendWholeNumber = true;
    state.legendMaxCharacters = 0;
    state.legendUseFullValues = false;
  },
  [t.LOCATIONS_ADD_RESULTS](state, response) {
    let locations = { ...state.locations };
    state.resultsIsLoading = false;
    // Set displayNoData to false when loading datasheet record results
    state.displayNoData = false;

    // Store categorical options if present
    if (
      response["recordType"] === "dropdown" ||
      response["recordType"] === "radio"
    ) {
      state.resultType = response["recordType"]; // Keep server's canonical type (dropdown or radio)

      // Extract the option IDs from location results based on aggregation
      const categoryValues = response["hydra:member"]
        .map((result) =>
          state.aggregationCategorical === "most_recent"
            ? result.most_recent_option_id
            : result.most_frequent_option_id,
        )
        .filter((val) => val !== null && val !== undefined);

      // Generate categorical legend
      const legend = legendCategoricalData(
        response.optionsValues,
        categoryValues,
      );
      state.legendRanges = legend.ranges;
      state.legendScale = legend.scale;
      state.legendMin = legend.min;
      state.legendMax = legend.max;
      state.legendWholeNumber = legend.wholeNumber;
      state.legendMaxCharacters = legend.maxCharacters;
      state.legendUseFullValues = legend.useFullValues;
    } else if (response["recordType"] === "number") {
      state.resultType = "number";

      // Generate numeric legend based on current aggregation method
      const values = response["hydra:member"]
        .map((result) => result[state.aggregationNumeric])
        .filter((val) => val !== null && val !== undefined);

      const legend = legendNumericData(values, state.aggregationNumeric);
      state.legendRanges = legend.ranges;
      state.legendScale = legend.scale;
      state.legendMin = legend.min;
      state.legendMax = legend.max;
      state.legendWholeNumber = legend.wholeNumber;
      state.legendMaxCharacters = legend.maxCharacters;
      state.legendUseFullValues = legend.useFullValues;
    }

    response["hydra:member"].forEach((result) => {
      const existingLocation = locations[result["area_id"]] || {};
      const existingResults = existingLocation.results || {};

      locations[result["area_id"]] = {
        ...existingLocation,
        id: result.area_id,
        "@id": `areas/${result.area_id}`,
        results: {
          ...existingResults,
          [state.resultId]: result,
        },
      };
    });
    state.locations = { ...locations };
  },

  [t.LOCATIONS_START_LOADING_DATASHEET_RESULTS](state, datasheet) {
    state.message = null;
    state.messageType = null;

    state.resultType = "datasheet";
    state.resultId = datasheet.id;
    state.resultLabel = datasheet.name;
    state.resultDescription = datasheet.description;
    state.resultsIsLoading = true;
    state.displayNoData = false;

    state.legendRanges = [];
    state.legendScale = null;
    state.legendMin = null;
    state.legendMax = null;
    state.legendWholeNumber = true;
    state.legendMaxCharacters = 0;
    state.legendUseFullValues = false;
  },
  [t.LOCATIONS_ADD_DATASHEET_RESULTS](state, response) {
    let locations = { ...state.locations };
    state.resultsIsLoading = false;
    state.resultType = "datasheet";

    let counts = [];

    response["hydra:member"].forEach((result) => {
      const existingLocation = locations[result["area_id"]] || {};
      const existingResults = existingLocation.results || {};

      locations[result["area_id"]] = {
        ...existingLocation,
        id: result.area_id,
        "@id": `areas/${result.area_id}`,
        results: {
          ...existingResults,
          [state.resultId]: {
            observationsTotal: result.observationsTotal,
            most_recent: result.most_recent,
          },
        },
      };
      counts.push(result.observationsTotal || 0);
    });

    state.locations = locations;

    const legend = legendObservationsCount(counts);
    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;
  },

  [t.LOCATIONS_START_LOADING_DATASHEET_USAGE](state) {
    state.message = null;
    state.messageType = null;
    state.resultType = "datasheet_usage";
    state.resultId = null;
    state.resultLabel = null;
    state.resultDescription = null;
    state.resultsIsLoading = true;
    state.displayNoData = true;
    state.legendRanges = [];
    state.legendScale = null;
    state.legendMin = null;
    state.legendMax = null;
    state.legendWholeNumber = true;
    state.legendMaxCharacters = 0;
    state.legendUseFullValues = false;
  },
  [t.LOCATIONS_ADD_DATASHEET_USAGE](state, response) {
    let locations = { ...state.locations };
    state.resultsIsLoading = false;
    state.resultType = "datasheet_usage";

    // Handle different possible response formats
    const locationResults = response["hydra:member"] || [];

    if (!Array.isArray(locationResults)) {
      console.error("locationResults is not an array:", locationResults);
      return;
    }

    // Collect unique datasheets
    const datasheetMap = {};
    locationResults.forEach((result) => {
      if (result.most_frequent_datasheet_id) {
        const id = result.most_frequent_datasheet_id; // Keep as string (GUID)
        datasheetMap[id] = result.most_frequent_datasheet_name;
      }
      if (result.most_recent_datasheet_id) {
        const id = result.most_recent_datasheet_id; // Keep as string (GUID)
        datasheetMap[id] = result.most_recent_datasheet_name;
      }
    });

    // Create sorted list of datasheets formatted for legendCategoricalData
    // legendCategoricalData expects: { id, value, orderNumber }
    const datasheets = Object.entries(datasheetMap)
      .map(([id, name]) => ({
        id: id, // Keep as string (GUID)
        value: name, // legendCategoricalData expects 'value' property
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((ds, index) => ({
        ...ds,
        orderNumber: index + 1,
      }));

    // Extract the datasheet IDs from location results based on aggregation
    const datasheetValues = locationResults
      .map((result) =>
        state.aggregationDatasheetUsage === "most_recent"
          ? result.most_recent_datasheet_id
          : result.most_frequent_datasheet_id,
      )
      .filter((val) => val !== null && val !== undefined);

    const legend = legendCategoricalData(datasheets, datasheetValues);
    state.legendRanges = legend.ranges;
    state.legendScale = legend.scale;
    state.legendMin = legend.min;
    state.legendMax = legend.max;
    state.legendWholeNumber = legend.wholeNumber;
    state.legendMaxCharacters = legend.maxCharacters;
    state.legendUseFullValues = legend.useFullValues;

    // Update locations with datasheet usage data
    locationResults.forEach((result) => {
      const existingLocation = locations[result["area_id"]] || {};

      locations[result["area_id"]] = {
        ...existingLocation,
        id: result.area_id,
        "@id": `areas/${result.area_id}`,
        datasheetUsage: {
          most_frequent_datasheet_id: result.most_frequent_datasheet_id || null, // Keep as string (GUID)
          most_frequent_datasheet_name: result.most_frequent_datasheet_name,
          most_frequent_count: result.most_frequent_count,
          most_recent_datasheet_id: result.most_recent_datasheet_id || null, // Keep as string (GUID)
          most_recent_datasheet_name: result.most_recent_datasheet_name,
        },
      };
    });
    state.locations = { ...locations };
  },

  [t.LOCATIONS_SET_MARKERS_IN_VIEW](state, count) {
    state.markersInView = count;
  },
};

/**
 * LocationBuffer handles batching location data before committing to state.
 * - First page: commits immediately
 * - Subsequent pages: buffers and commits every 10 pages (10,000 items)
 * - Flush: commits any remaining buffered data
 */
class LocationBuffer {
  constructor(commitFn) {
    this.commitFn = commitFn;
    this.buffer = [];
    this.pageCount = 0;
    this.totalItems = null;
  }

  addPage(data) {
    this.pageCount++;
    this.totalItems = data["hydra:totalItems"];

    if (this.pageCount === 1) {
      // First page: commit immediately
      this.commitFn(data);
    } else {
      // Subsequent pages: buffer
      this.buffer.push(...data["hydra:member"]);

      // Commit every 10 pages
      if (this.pageCount % 10 === 1) {
        this.flush();
      }
    }
  }

  flush() {
    if (this.buffer.length > 0) {
      this.commitFn({
        "hydra:member": this.buffer,
        "hydra:totalItems": this.totalItems,
      });
      this.buffer = [];
    }
  }
}

const actions = {
  resetMap({ commit }) {
    commit(t.LOCATIONS_RESET);
  },
  async fetchAllProjectLocations({ commit, state, rootGetters }) {
    const projectIdRoot = rootGetters["project/get/id"];
    commit(t.LOCATIONS_START_LOADING, projectIdRoot);

    const buffer = new LocationBuffer((data) => commit(t.LOCATIONS_ADD, data));

    try {
      // to avoid loading too many at once,
      // fetch all locations in blocks of 1000
      const itemsPerPage = 1000;
      let url = `/projects/${projectIdRoot}/locations?order[observationsTotal]=desc&page=1&itemsPerPage=${itemsPerPage}`;

      while (url) {
        const response = await fetch(url);
        if (!response.ok) {
          const error = await response.text();
          buffer.flush();
          commit(t.LOCATIONS_SET_MESSAGE, {
            message: error,
            messageType: "danger",
          });
          break;
        }
        const data = await response.json();
        buffer.addPage(data);
        commit(
          t.LOCATIONS_UPDATE_LOADED_COUNT,
          buffer.pageCount * itemsPerPage,
        );

        if (data["hydra:view"] && data["hydra:view"]["hydra:next"]) {
          url = data["hydra:view"]["hydra:next"];
        } else {
          url = null;
          break;
        }
      }
    } catch (error) {
      console.log(error);
      buffer.flush();
      commit(t.LOCATIONS_SET_MESSAGE, {
        message: error.message || "Error fetching project locations",
        messageType: "danger",
      });
    } finally {
      buffer.flush();
      commit(t.LOCATIONS_FINISH_LOADING);
    }
  },
  getDatasheetRecordLocationResults({ commit, state }, datasheetRecord) {
    commit(t.LOCATIONS_START_LOADING_RESULTS, datasheetRecord);
    return fetch(
      "/datasheet_records/" + datasheetRecord.id + "/location_results",
    )
      .then((response) => response.json())
      .then((data) => {
        commit(t.LOCATIONS_ADD_RESULTS, data);
        //console.log(data)
        return data;
      })
      .catch((error) => {
        commit(t.LOCATIONS_SET_MESSAGE, {
          message: error.message,
          messageType: "danger",
        });
      });
  },
  getDatasheetLocationResults({ commit, state }, datasheet) {
    commit(t.LOCATIONS_START_LOADING_DATASHEET_RESULTS, datasheet);
    return fetch("/datasheets/" + datasheet.id + "/location_results")
      .then((response) => response.json())
      .then((data) => {
        commit(t.LOCATIONS_ADD_DATASHEET_RESULTS, data);
        return data;
      })
      .catch((error) => {
        commit(t.LOCATIONS_SET_MESSAGE, {
          message: error.message,
          messageType: "danger",
        });
      });
  },
  getObservationLocationResults({ commit }) {
    commit(t.LOCATIONS_SET_OBSERVATIONS);
  },
  getDatasheetUsageLocationResults({ commit, state, rootGetters }) {
    commit(t.LOCATIONS_START_LOADING_DATASHEET_USAGE);
    const projectId = rootGetters["project/get/id"];
    return fetch(`/projects/${projectId}/location_datasheet_usage`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        commit(t.LOCATIONS_ADD_DATASHEET_USAGE, data);
        return data;
      })
      .catch((error) => {
        console.error("Error fetching datasheet usage:", error);
        commit(t.LOCATIONS_SET_MESSAGE, {
          message: error.message,
          messageType: "danger",
        });
      });
  },
};

export default {
  namespaced: true,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations,
};
